import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay, testId }) => (
    <span className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
        <motion.span
            data-testid={testId}
            className="block will-change-transform"
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.25, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

const Asterisk = () => (
    <motion.svg
        viewBox="0 0 100 100"
        className="h-[0.55em] w-[0.55em] animate-spin-slow text-lavender"
        fill="currentColor"
        aria-hidden="true"
        initial={{ scale: 0, rotate: -90 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ duration: 1.1, delay: 1.15, ease: EASE }}
    >
        <path d="M50 0 L58 38 L95 25 L64 52 L92 82 L55 66 L50 100 L45 66 L8 82 L36 52 L5 25 L42 38 Z" />
    </motion.svg>
);

export const Hero = () => {
    const frameRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: frameRef,
        offset: ["start end", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

    return (
        <section
            data-testid="hero-section"
            className="relative grid min-h-screen grid-cols-1 items-center gap-16 px-6 pb-16 pt-28 md:grid-cols-12 md:px-12 md:pb-20 md:pt-32"
        >
            <div className="md:col-span-7">
                <motion.p
                    data-testid="hero-kicker"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.9, delay: 0.3 }}
                    className="label-xs mb-8 flex items-center gap-4 text-ink/50"
                >
                    <span className="h-px w-10 bg-ink/30" aria-hidden="true" />
                    A quick intermission
                </motion.p>

                <h1
                    data-testid="hero-headline"
                    className="font-display text-[13vw] font-medium leading-[0.92] tracking-[-0.02em] text-ink md:text-[6.75rem]"
                >
                    <MaskedLine delay={0.35} testId="headline-line-1">
                        We&rsquo;ll be
                    </MaskedLine>
                    <MaskedLine delay={0.5} testId="headline-line-2">
                        <span className="inline-flex items-center gap-[0.12em]">
                            back
                            <Asterisk />
                        </span>
                    </MaskedLine>
                    <MaskedLine delay={0.65} testId="headline-line-3">
                        <em className="font-medium italic text-violet">soon.</em>
                    </MaskedLine>
                </h1>

                <motion.p
                    data-testid="hero-subtext"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 1.05, ease: EASE }}
                    className="mt-10 max-w-md text-base leading-relaxed text-ink/60 md:text-lg"
                >
                    RentOui — the peer-to-peer wardrobe for designer handbags —
                    is off stage for a quick polish. Merci for your patience.
                </motion.p>
            </div>

            <motion.figure
                ref={frameRef}
                data-testid="hero-image-frame"
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.3, delay: 0.85, ease: EASE }}
                className="md:col-span-5"
            >
                <div className="relative aspect-[4/5] overflow-hidden md:aspect-auto md:h-[64vh]">
                    <motion.img
                        src="/assets/hero-photo.webp"
                        alt="Reading the RentOui news in a violet convertible by the water"
                        style={{ y: imgY }}
                        className="h-[116%] w-full scale-105 object-cover"
                        data-testid="hero-image"
                    />
                </div>
                <figcaption
                    className="label-xs mt-4 flex justify-between text-ink/40"
                    data-testid="hero-image-caption"
                >
                    <span>Fig. 01 — Off duty</span>
                    <span>FW 26</span>
                </figcaption>
            </motion.figure>
        </section>
    );
};
